import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';

import IntegrationHeroCard from 'dashboard/components/IntegrationHeroCard';
import OauthPopup from 'dashboard/components/OauthPopup';
import { Container, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { CreateExternalIntegrationParams } from 'dashboard/redux/thunk-actions/external-integrations';
import ExternalIntegrationHeader from 'dashboard/views/DashboardExternalIntegrationsShowView/ExternalIntegrationHeader';
import { ProductFeatureContactUsBanner } from 'dashboard/views/DashboardExternalIntegrationsShowView/ProductFeatureContactUsBanner';
import ExternalIntegrationOAuthDetailsTable from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ExternalIntegrationOAuthDetailsTable';
import ExternalIntegrationOAuthErrorModal from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ExternalIntegrationOAuthErrorModal';
import Button from 'design-system/components/Button';
import SalesforceIcon from 'images/icons/marketplace/SalesforceIcon.svg';
import {
  LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
  LEGACY_EXTERNAL_INTEGRATION_VENDORS,
  LegacyVendorName,
} from 'lib/constants/externalIntegrationVendors';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

export interface SalesforceConfigurationProps {
  editable: boolean;
  isLoading: boolean;
  externalIntegrations: ExternalIntegration[];
  createExternalIntegration: (
    externalIntegrationType: ExternalIntegration['type'],
    integrationParams: CreateExternalIntegrationParams,
  ) => void;
  setSelectedIntegration: (integration: ExternalIntegration) => void;
}

const OAUTH_CLIENT_ID =
  '3MVG9p1Q1BCe9GmCDId1PFkhkrz9cCtlNDlfTyMQrBpVI33iayOsWFS48Nlypt0ZeNrtU4EB6kzRr9zmxMGvp';

const SalesforceConfiguration = ({
  editable,
  isLoading,
  externalIntegrations,
  createExternalIntegration,
  setSelectedIntegration,
}: SalesforceConfigurationProps) => {
  const [showErrorModal, { enable: openErrorModal, disable: closeErrorModal }] = useToggle(false);
  const authorizationUrl = `https://login.salesforce.com/services/oauth2/authorize?client_id=${OAUTH_CLIENT_ID}&redirect_uri=${LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL}&response_type=code&prompt=consent&display=popup`;
  const testAuthorizationUrl = `https://test.salesforce.com/services/oauth2/authorize?client_id=${OAUTH_CLIENT_ID}&redirect_uri=${LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL}&response_type=code&prompt=consent&display=popup`;
  const salesforceVendor = LEGACY_EXTERNAL_INTEGRATION_VENDORS.find(
    (vendor) => vendor.name === LegacyVendorName.Salesforce,
  );

  return (
    <Container>
      <ExternalIntegrationOAuthErrorModal isOpen={showErrorModal} handleClose={closeErrorModal} />
      <ExternalIntegrationHeader editable={editable}>
        <OauthPopup
          url={testAuthorizationUrl}
          onCode={(code: string) => {
            createExternalIntegration(ApiModel.ExternalIntegrationProductivitySalesforceOAuth, {
              code: code,
              'redirect-url': LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
              host: 'test.salesforce.com',
            });
          }}
          onError={openErrorModal}
          disabled={!editable}
        >
          <Button
            id='salesforce__add-test-connection-button'
            width='auto'
            variant='basic'
            aria-label='add test connection'
            disabled={!editable}
            prefixIcon={<IconPlus />}
          >
            Add Test Connection
          </Button>
        </OauthPopup>
        <OauthPopup
          url={authorizationUrl}
          onCode={(code: string) => {
            createExternalIntegration(ApiModel.ExternalIntegrationProductivitySalesforceOAuth, {
              code: code,
              'redirect-url': LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
              host: 'login.salesforce.com',
            });
          }}
          onError={openErrorModal}
          disabled={!editable}
        >
          <Button
            id='salesforce__add-connection-button'
            width='auto'
            aria-label='add connection'
            disabled={!editable}
            prefixIcon={<IconPlus />}
          >
            Add Connection
          </Button>
        </OauthPopup>
      </ExternalIntegrationHeader>
      <Section>
        <ProductFeatureContactUsBanner />
        <IntegrationHeroCard title='Salesforce' image={SalesforceIcon}>
          {salesforceVendor != null && salesforceVendor.description}
        </IntegrationHeroCard>
        <ExternalIntegrationOAuthDetailsTable
          isLoading={isLoading}
          editable={editable}
          displayName='Salesforce'
          connectionName='Instance'
          externalIntegrations={externalIntegrations}
          setSelectedIntegration={setSelectedIntegration}
        />
      </Section>
    </Container>
  );
};

export default SalesforceConfiguration;
